import React, {
    type ChangeEventHandler,
    type FocusEventHandler,
    type ForwardedRef,
    forwardRef,
    type InputHTMLAttributes,
    type MouseEventHandler,
} from 'react';

import { PrimitiveInputText } from './InputText.style';

// This is a subset of `HTMLInputTypeAttribute`.
export type InputTextType = 'email' | 'password' | 'search' | 'text' | 'url';
export type InputTextMode = InputHTMLAttributes<HTMLInputElement>['inputMode'];

export type InputTextFocus = FocusEventHandler<HTMLInputElement>;
export type InputTextChange = ChangeEventHandler<HTMLInputElement>;

export interface InputTextProps {
    id?: string;
    name?: string;
    type?: InputTextType;
    inputMode?: InputTextMode;
    pattern?: string;
    value?: string | number;
    placeholder?: string;
    ariaLabel?: string;
    autoFocus?: boolean;
    disabled?: boolean;
    // Required to add a `data-input-error` attribute for easier styling.
    error?: boolean;
    readOnly?: boolean;
    required?: boolean;
    safePrevention?: boolean;
    onBlur?: InputTextFocus;
    onFocus?: InputTextFocus;
    onChange?: InputTextChange;
    // Required for `styled-components` to append additional classes.
    className?: string;
}

function InputTextComponent(
    {
        type = 'text',
        value = '',
        pattern = '',
        disabled = false,
        error = false,
        readOnly = false,
        safePrevention = false,
        ariaLabel,
        onBlur,
        onFocus,
        onChange,
        ...rest
    }: InputTextProps,
    ref: ForwardedRef<HTMLInputElement>,
) {
    const handleSafePrevention: MouseEventHandler<HTMLInputElement> = (event) => {
        // Do we actually need to support the `safePrevention` prop?
        event.preventDefault();
        event.stopPropagation();
    };

    const noInteraction = Boolean(disabled || readOnly);

    return (
        <PrimitiveInputText
            ref={ref}
            {...rest}
            data-input-error={error ? true : undefined}
            type={type}
            value={value}
            aria-label={ariaLabel}
            pattern={pattern.trim().length ? pattern : undefined}
            disabled={Boolean(disabled || !onChange)}
            readOnly={Boolean(readOnly || !onChange)}
            onBlur={noInteraction ? undefined : onBlur}
            onFocus={noInteraction ? undefined : onFocus}
            onChange={noInteraction ? undefined : onChange}
            onClick={safePrevention ? handleSafePrevention : undefined}
        />
    );
}

export const InputText = forwardRef(InputTextComponent);
